/**
 * The NLM journal list: reading the compact asset, searching it, and the ISO 4 dots.
 *
 * The asset (`public/data/journal-abbreviations.txt`, built by
 * `scripts/build-journal-abbreviations.mjs` from NLM's J_Medline.txt) is one journal
 * per line — `title|medAbbr|isoAbbr|issnPrint|issnOnline` — with three economies the
 * reader below undoes: a MEDLINE abbreviation identical to the title is stored as "=",
 * an ISO abbreviation identical to the MEDLINE one is left empty, and ISSNs lose their
 * hyphen. Lines starting with "#" are the header (source, date, field list).
 */

export interface JournalRecord {
  /** NLM's title, in NLM's capitalisation: "The New England journal of medicine". */
  title: string;
  /** MEDLINE (NLM) title abbreviation: "N Engl J Med". */
  medAbbr: string;
  /** The ISO abbreviation as NLM records it — today, without periods. */
  isoAbbr: string;
  issnPrint: string;
  issnOnline: string;
}

export interface JournalList {
  /** ISO date of the NLM file the asset was built from, if the header says. */
  updated: string;
  records: JournalRecord[];
}

const RE_LINE_BREAK = /\r?\n/;
const RE_UPDATED = /^#\s*updated:\s*(\S+)/i;

function withHyphen(issn: string): string {
  return issn.length === 8 ? `${issn.slice(0, 4)}-${issn.slice(4)}` : issn;
}

export function parseJournalAsset(text: string): JournalList {
  let updated = "";
  const records: JournalRecord[] = [];
  for (const line of text.split(RE_LINE_BREAK)) {
    if (line === "") {
      continue;
    }
    if (line.startsWith("#")) {
      updated = RE_UPDATED.exec(line)?.[1] ?? updated;
      continue;
    }
    const [title = "", med = "", iso = "", print = "", online = ""] =
      line.split("|");
    const medAbbr = med === "=" ? title : med;
    records.push({
      title,
      medAbbr,
      isoAbbr: iso === "" ? medAbbr : iso,
      issnPrint: withHyphen(print),
      issnOnline: withHyphen(online),
    });
  }
  return { updated, records };
}

/* ----------------------------------------------------------------- search */

const RE_COMBINING_MARKS = /[\u0300-\u036f]/g;
const RE_APOSTROPHES = /['\u2018\u2019]/g;
const RE_AMPERSAND = /&/g;
const RE_NON_ALNUM = /[^a-z0-9]+/g;
const RE_TRAILING_QUALIFIER = /\s*\([^()]*\)\s*$/;
const RE_LEADING_ARTICLE = /^(?:the|a|an) /;
const RE_ISSN_QUERY = /^\d{4}\s?\d{3}[\dx]$/;
const RE_SPACE = / /g;

/** Case-, accent- and punctuation-blind: "J. Am. Chem. Soc." → "j am chem soc". */
export function foldForSearch(text: string): string {
  return text
    .normalize("NFD")
    .replace(RE_COMBINING_MARKS, "")
    .toLowerCase()
    .replace(RE_APOSTROPHES, "")
    .replace(RE_AMPERSAND, " and ")
    .replace(RE_NON_ALNUM, " ")
    .trim();
}

interface IndexedJournal {
  record: JournalRecord;
  /** The folded title with NLM's trailing qualifier and a leading article removed. */
  core: string;
  title: string;
  abbr: string;
  issns: string[];
}

export interface JournalIndex {
  entries: IndexedJournal[];
}

export function buildJournalIndex(
  records: readonly JournalRecord[]
): JournalIndex {
  return {
    entries: records.map((record) => {
      const title = foldForSearch(record.title);
      const core = foldForSearch(
        record.title.replace(RE_TRAILING_QUALIFIER, "")
      ).replace(RE_LEADING_ARTICLE, "");
      return {
        record,
        core,
        title,
        abbr: foldForSearch(record.medAbbr),
        issns: [record.issnPrint, record.issnOnline]
          .filter(Boolean)
          .map((issn) => issn.replace("-", "").toLowerCase()),
      };
    }),
  };
}

/** 0 exact · 1 starts with · 2 starts a word · 3 anywhere · 4 every word somewhere. */
export type MatchRank = 0 | 1 | 2 | 3 | 4;

export interface JournalMatch {
  record: JournalRecord;
  rank: MatchRank;
}

function rankText(haystack: string, query: string): MatchRank | null {
  if (haystack === query) {
    return 0;
  }
  if (haystack.startsWith(query)) {
    return 1;
  }
  if (haystack.includes(` ${query}`)) {
    return 2;
  }
  return haystack.includes(query) ? 3 : null;
}

function bestRank(ranks: (MatchRank | null)[]): MatchRank | null {
  let best: MatchRank | null = null;
  for (const rank of ranks) {
    if (rank !== null && (best === null || rank < best)) {
      best = rank;
    }
  }
  return best;
}

function rankEntry(
  entry: IndexedJournal,
  query: string,
  words: string[]
): MatchRank | null {
  const direct = bestRank([
    rankText(entry.core, query),
    rankText(entry.abbr, query),
    rankText(entry.title, query),
  ]);
  if (direct !== null) {
    return direct;
  }
  if (words.length < 2) {
    return null;
  }
  const everyWord = (haystack: string) =>
    words.every((word) => haystack.includes(word));
  return everyWord(entry.title) || everyWord(entry.abbr) ? 4 : null;
}

/**
 * Search titles and abbreviations in both directions, ranked exact → prefix → word →
 * substring → all-words, then shorter titles first. An ISSN-shaped query matches the
 * print or online ISSN exactly.
 */
export function searchJournals(
  index: JournalIndex,
  rawQuery: string,
  limit = 25
): { total: number; matches: JournalMatch[] } {
  const query = foldForSearch(rawQuery);
  if (query.length < 2) {
    return { total: 0, matches: [] };
  }
  const issn = RE_ISSN_QUERY.test(query) ? query.replace(RE_SPACE, "") : null;
  const words = query.split(" ");
  const found: JournalMatch[] = [];
  for (const entry of index.entries) {
    let rank: MatchRank | null = null;
    if (issn) {
      rank = entry.issns.includes(issn) ? 0 : null;
    } else {
      rank = rankEntry(entry, query, words);
    }
    if (rank !== null) {
      found.push({ record: entry.record, rank });
    }
  }
  found.sort(
    (a, b) =>
      a.rank - b.rank ||
      a.record.title.length - b.record.title.length ||
      a.record.title.localeCompare(b.record.title, "en")
  );
  return { total: found.length, matches: found.slice(0, limit) };
}

/** Exact title → record, for turning PubMed's journal titles into abbreviations. */
export function journalsByTitle(
  records: readonly JournalRecord[]
): Map<string, JournalRecord> {
  const map = new Map<string, JournalRecord>();
  for (const record of records) {
    const key = foldForSearch(record.title);
    if (!map.has(key)) {
      map.set(key, record);
    }
  }
  return map;
}

/* ------------------------------------------------------------- ISO 4 dots */

const RE_TOKEN_STRIP = /[^\p{L}\p{N}]/gu;
const RE_WHITESPACE = /\s+/;
const RE_HYPHENS = /[-\u2010-\u2015]/;

function bareToken(token: string): string {
  return token
    .normalize("NFD")
    .replace(RE_COMBINING_MARKS, "")
    .replace(RE_TOKEN_STRIP, "")
    .toLowerCase();
}

interface TitleWords {
  /** Every title word, and each half of a hyphenated one ("Naunyn-Schmiedeberg's"). */
  words: Set<string>;
  /**
   * Words the title itself abbreviates — "ed." in "(International ed. in English)" —
   * told apart from a section break ("The Lancet. Oncology") by the lower-case word
   * that follows.
   */
  abbreviated: Set<string>;
}

const RE_STARTS_LOWER = /^\p{Ll}/u;

function titleWordSet(title: string): TitleWords {
  const words = new Set<string>();
  const abbreviated = new Set<string>();
  const tokens = title.split(RE_WHITESPACE).filter(Boolean);
  for (const [index, token] of tokens.entries()) {
    for (const piece of [token, ...token.split(RE_HYPHENS)]) {
      const bare = bareToken(piece);
      if (bare !== "") {
        words.add(bare);
      }
    }
    const next = tokens[index + 1] ?? "";
    if (token.endsWith(".") && RE_STARTS_LOWER.test(next)) {
      abbreviated.add(bareToken(token));
    }
  }
  return { words, abbreviated };
}

/** "natl" shortens "national": same first letter, letters in order, fewer of them. */
function shortens(abbreviation: string, word: string): boolean {
  if (abbreviation.length >= word.length || abbreviation[0] !== word[0]) {
    return false;
  }
  let position = 0;
  for (const letter of word) {
    if (letter === abbreviation[position]) {
      position += 1;
      if (position === abbreviation.length) {
        return true;
      }
    }
  }
  return false;
}

function dotToken(token: string, title: TitleWords): string {
  const bare = bareToken(token);
  if (bare === "" || token.endsWith(".")) {
    return token;
  }
  if (title.words.has(bare)) {
    return title.abbreviated.has(bare) ? `${token}.` : token;
  }
  for (const word of title.words) {
    if (shortens(bare, word)) {
      return `${token}.`;
    }
  }
  return token;
}

/**
 * The abbreviation with ISO 4 punctuation: a full stop after every word the
 * abbreviation shortens, none after a word kept whole. "J Am Chem Soc" for "Journal of
 * the American Chemical Society" → "J. Am. Chem. Soc."; "Lancet Oncol" for "The Lancet.
 * Oncology" → "Lancet Oncol.". Derived by comparing each word with the title — a
 * word that matches nothing in the title, and anything in parentheses, is left alone.
 */
export function isoDotted(abbreviation: string, title: string): string {
  const titleWords = titleWordSet(title);
  let depth = 0;
  return abbreviation
    .split(RE_WHITESPACE)
    .filter(Boolean)
    .map((token) => {
      if (token.startsWith("(")) {
        depth += 1;
      }
      const inside = depth > 0;
      if (token.endsWith(")")) {
        depth = Math.max(0, depth - 1);
      }
      return inside ? token : dotToken(token, titleWords);
    })
    .join(" ");
}
